import React, { useState, useEffect } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw, convertFromHTML } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { AlertCircle } from 'lucide-react';

interface PrerequisiteEditorProps {
  prerequisiteContent?: string;
  onPrerequisiteContentChange: (content: string) => void;
}

const PrerequisiteEditor: React.FC<PrerequisiteEditorProps> = ({
  prerequisiteContent,
  onPrerequisiteContentChange
}) => {
  const [hasPrerequisite, setHasPrerequisite] = useState(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  // Inicializar editor com conteúdo existente
  useEffect(() => {
    if (prerequisiteContent) {
      setHasPrerequisite(true);
      try {
        const blocksFromHTML = convertFromHTML(prerequisiteContent);
        const contentState = ContentState.createFromBlockArray(
          blocksFromHTML.contentBlocks,
          blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
      } catch (error) {
        console.error('Erro ao carregar pré-requisito:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    onPrerequisiteContentChange(html);
  };

  const handleTogglePrerequisite = (enabled: boolean) => {
    setHasPrerequisite(enabled);
    if (!enabled) {
      setEditorState(EditorState.createEmpty());
      onPrerequisiteContentChange('');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-tinta">
          Pré-requisito do Sorteio
        </label>
        <p className="text-xs text-tinta-soft italic mt-1 mb-3">
          Exige confirmação antes de participar (opcional)
        </p>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            type="button"
            onClick={() => handleTogglePrerequisite(false)}
            className={`px-3 py-2.5 border rounded-lg transition-colors text-xs sm:text-sm font-bold ${
              !hasPrerequisite
                ? 'border-bordo bg-bordo/10 text-bordo-dark'
                : 'border-tinta/15 bg-white text-tinta-soft hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <div className="flex items-center justify-center">
              <span>Nenhum</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleTogglePrerequisite(true)}
            className={`px-3 py-2.5 border rounded-lg transition-colors text-xs sm:text-sm font-bold ${
              hasPrerequisite
                ? 'border-bordo bg-bordo/10 text-bordo-dark'
                : 'border-tinta/15 bg-white text-tinta-soft hover:bg-creme-light hover:text-tinta'
            }`}
          >
            <div className="flex items-center justify-center space-x-1.5">
              <AlertCircle className="h-4 w-4" />
              <span>Definir</span>
            </div>
          </button>
        </div>
      </div>

      {hasPrerequisite && (
        <div className="mt-3 pl-4 border-l-2 border-bordo/30 space-y-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-tinta">
            Conteúdo do Pré-requisito
          </label>
          <div className="border border-tinta/15 rounded-lg overflow-hidden bg-white">
            <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorChange}
              toolbar={{
                options: ['inline', 'list', 'link', 'history'],
                inline: {
                  options: ['bold', 'italic']
                },
                list: {
                  options: ['unordered', 'ordered']
                },
                link: {
                  options: ['link']
                }
              }}
              locale='pt'
              editorClassName="min-h-[150px] px-3 py-2 text-sm"
              placeholder="Ex: Siga nosso Instagram @suaempresa"
            />
          </div>
          <p className="mt-1 text-xs text-tinta-soft italic">
            O usuário precisará confirmar antes de participar
          </p>
        </div>
      )}
    </div>
  );
};

export default PrerequisiteEditor;
